/**
 * Touren: reine Logik, ohne React und ohne UI-Importe, damit `node --test` sie lädt.
 * Die Verwaltungsseite (Betrieb → Touren) und die Fahreransicht (Meine Tour) laden dieselbe Datei.
 *
 * Es gibt keine Tourenberechnung: MC-ERP schlägt nur "nächster Halt zuerst" vor (Luftlinie), der Planer ändert die
 * Reihenfolge von Hand. Die Navigation ist ein Google-Maps-Link; den Verkehr zeigt Google, wir rechnen ihn nie.
 * In den Links stehen nur Koordinaten, keine Namen und keine Personendaten.
 */

export interface Coords { lat: number; lon: number }
/** Alles mit Koordinaten, die fehlen dürfen (Standort ohne Geodaten). */
export interface MaybeCoords { lat?: number | null; lon?: number | null }

/** Eine Markierung der Lieferstelle (ops.Lieferstelle). Nur die Felder, die die Haltepunkt-Wahl braucht. */
export interface TourMark {
  lieferstelleId: string
  kind: string
  lat: number
  lon: number
  sortOrder?: number
}

export type StopPointSource = 'gewaehlt' | 'parkplatz' | 'anlieferung' | 'eingang' | 'standort' | 'keiner'

export interface StopPoint {
  /** Wohin der Fahrer fährt. null, wenn weder Markierung noch Standort Koordinaten haben. */
  point: Coords | null
  source: StopPointSource
  /** Id der Markierung hinter dem Punkt; null beim Standort. */
  lieferstelleId: string | null
  /** true: 2 oder mehr Parkplätze und keiner gewählt. Der Fahrer wählt den Parkplatz ("Parkplatz wählen"). */
  needsParkingChoice: boolean
}

/** Google Maps nimmt auf dem Handy höchstens 3 Zwischenziele je Link. */
export const MAX_WAYPOINTS = 3

const EARTH_RADIUS_M = 6371008.8

export function hasCoords(c: MaybeCoords | null | undefined): c is Coords {
  return !!c && typeof c.lat === 'number' && typeof c.lon === 'number' && Number.isFinite(c.lat) && Number.isFinite(c.lon)
}

/** Luftlinie in Metern (Haversine). */
export function distanceMeters(a: Coords, b: Coords): number {
  const rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad
  const dLon = (b.lon - a.lon) * rad
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

const bySortOrder = (a: TourMark, b: TourMark) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)

/**
 * Haltepunkt eines Halts: gewählte Markierung, sonst der Parkplatz (wenn es genau einen gibt), sonst Anlieferung,
 * sonst Eingang, sonst die Koordinaten des Standorts. Gleiche Regel wie die Fahreransicht (Parkplatz zuerst).
 * Bei 2 oder mehr Parkplätzen ohne Wahl ist `needsParkingChoice` true; der Punkt fällt dann auf Anlieferung/Eingang/Standort.
 * `chosenId` darf eine veraltete Id sein: sie wird dann ignoriert.
 */
export function stopPoint(location: MaybeCoords | null | undefined, marks: TourMark[], chosenId?: string | null): StopPoint {
  const usable = marks.filter(hasCoords).sort(bySortOrder)
  const chosen = chosenId == null ? undefined : usable.find(m => String(m.lieferstelleId) === String(chosenId) && m.kind !== 'hinweis' && m.kind !== 'weg')
  if (chosen) return { point: { lat: chosen.lat, lon: chosen.lon }, source: 'gewaehlt', lieferstelleId: chosen.lieferstelleId, needsParkingChoice: false }

  const parking = usable.filter(m => m.kind === 'parkplatz')
  if (parking.length === 1) {
    const m = parking[0]
    return { point: { lat: m.lat, lon: m.lon }, source: 'parkplatz', lieferstelleId: m.lieferstelleId, needsParkingChoice: false }
  }
  const needsParkingChoice = parking.length >= 2

  for (const kind of ['anlieferung', 'eingang'] as const) {
    const m = usable.find(x => x.kind === kind)
    if (m) return { point: { lat: m.lat, lon: m.lon }, source: kind, lieferstelleId: m.lieferstelleId, needsParkingChoice }
  }
  if (hasCoords(location)) return { point: { lat: location.lat, lon: location.lon }, source: 'standort', lieferstelleId: null, needsParkingChoice }
  return { point: null, source: 'keiner', lieferstelleId: null, needsParkingChoice }
}

/**
 * Vorschlag "nächster Halt zuerst": vom Start aus immer zum nächsten noch offenen Halt (Luftlinie).
 * Halte ohne Koordinaten bleiben am Ende, in ihrer bisherigen Reihenfolge. Hat der Start keine Koordinaten,
 * bleibt die Reihenfolge der Halte mit Koordinaten, wie sie ist. Gleiche Entfernung: der frühere Halt zuerst.
 * Gibt eine neue Liste zurück; die Eingabe bleibt unverändert.
 */
export function nearestFirst<T extends MaybeCoords>(start: MaybeCoords | null | undefined, stops: T[]): T[] {
  const located = stops.filter(hasCoords) as (T & Coords)[]
  const unlocated = stops.filter(s => !hasCoords(s))
  if (!hasCoords(start)) return [...located, ...unlocated]
  const rest = [...located]
  const out: T[] = []
  let current: Coords = start
  while (rest.length) {
    let best = 0
    let bestDistance = Infinity
    rest.forEach((s, i) => {
      const d = distanceMeters(current, s)
      if (d < bestDistance) { best = i; bestDistance = d }
    })
    const [next] = rest.splice(best, 1)
    out.push(next)
    current = next
  }
  return [...out, ...unlocated]
}

export interface TourLinkOptions {
  /**
   * Ohne `origin` startet die Maps-App an der Live-Position (Handy des Fahrers).
   * Mit `origin` (Startadresse oder Koordinaten) startet der erste Link dort; so zeigt Google am Planer-PC den Verkehr.
   */
  origin?: Coords | string | null
}

const fmt = (c: Coords) => `${Number(c.lat.toFixed(6))},${Number(c.lon.toFixed(6))}`

/**
 * Google-Maps-Links für eine Tour: ein Link je 3 Zwischenziele plus Ziel (Handy-Grenze). Teil n+1 beginnt dort,
 * wo Teil n endet. Der letzte Teil endet am Start, wenn `returnToStart` gilt und der Start Koordinaten hat.
 * Halte ohne Koordinaten fehlen in den Links (siehe hasCoords). Ohne Halt mit Koordinaten gibt es keinen Link.
 * Das `|` zwischen den Zwischenzielen schreibt der Browser beim Öffnen selbst als %7C.
 */
export function tourLinks(start: MaybeCoords | null | undefined, stops: MaybeCoords[], returnToStart: boolean, opts: TourLinkOptions = {}): string[] {
  const points: Coords[] = stops.filter(hasCoords).map(s => ({ lat: s.lat, lon: s.lon }))
  if (!points.length) return []
  if (returnToStart && hasCoords(start)) points.push({ lat: start.lat, lon: start.lon })

  let origin: string | null = null
  if (typeof opts.origin === 'string') origin = opts.origin.trim() ? encodeURIComponent(opts.origin.trim()) : null
  else if (hasCoords(opts.origin)) origin = fmt(opts.origin)

  const links: string[] = []
  for (let i = 0; i < points.length; i += MAX_WAYPOINTS + 1) {
    const part = points.slice(i, i + MAX_WAYPOINTS + 1)
    const destination = part[part.length - 1]
    const waypoints = part.slice(0, -1)
    let url = 'https://www.google.com/maps/dir/?api=1'
    if (origin) url += `&origin=${origin}`
    url += `&destination=${fmt(destination)}`
    if (waypoints.length) url += `&waypoints=${waypoints.map(fmt).join('|')}`
    url += '&travelmode=driving'
    links.push(url)
    origin = fmt(destination)
  }
  return links
}
